import React, { useState, useEffect } from 'react';
import {
  List,
  Datagrid,
  TextField,
  SearchInput,
  TopToolbar,
  SelectInput,
  BooleanField,
  FunctionField,
  EditButton,
  ShowButton,
  ReferenceField,
  useListContext,
  Filter,
  TextInput,
  DeleteButton,
  DeleteWithConfirmButton,
} from 'react-admin';
import { Card, Typography, Box, Chip } from '@mui/material';
import {
  Subscriptions as SubscriptionsIcon,
  Factory as FactoryIcon,
  Delete,
} from '@mui/icons-material';
import DeleteWithPasswordButton from '@/components/DeleteWithPasswordButton';
import CustomCreateButton from '@/components/CustomCreateButton';
import HierarchicalFilter, {
  HierarchicalFilterSelections,
} from '@/components/HierarchicalFilter';
import { TaskDataSourceType, TaskFunctionType } from '@/api/gen/model';
import { formatTimeSpanExpression } from '@/components/formatTimeSpane';

const SubscriptionListActions = () => (
  <TopToolbar
    sx={{
      alignItems: 'flex-end',
    }}
  >
    <CustomCreateButton
      variant='contained'
      label='Crear Suscripción'
      sx={{
        height: '40px',
        backgroundColor: 'primary.main',
        '&:hover': {
          backgroundColor: 'primary.dark',
        },
      }}
    />
  </TopToolbar>
);

const SubscriptionFilter = (props: any) => (
  <Filter {...props}>
    <SearchInput
      source='q'
      placeholder='Buscar suscripciones...'
      alwaysOn
      size='small'
      sx={{
        minWidth: 250,
        mb: 0.5,
      }}
    />
    <TextInput
      label='Referencia SCADA'
      source='ScadaReferenceName'
      size='small'
      resettable
    />
    <TextInput label='Unidad' source='UnitChange' size='small' resettable />
    <SelectInput
      source='DataSourceType'
      label='Tipo de Fuente'
      choices={[
        { id: TaskDataSourceType.VARIABLE, name: 'Variable' },
        { id: TaskDataSourceType.CSV_COLUMN, name: 'Columna CSV' },
      ]}
      size='small'
      resettable
    />
    <SelectInput
      source='InstantEnabled'
      label='Instantáneo Habilitado'
      choices={[
        { id: 'true', name: 'Sí' },
        { id: 'false', name: 'No' },
      ]}
      size='small'
      resettable
    />
    <SelectInput
      source='InstantCalculationType'
      label='Tipo de Cálculo'
      choices={[
        { id: TaskFunctionType.AVG, name: 'Promedio' },
        { id: TaskFunctionType.SUM, name: 'Suma' },
        { id: TaskFunctionType.MAX, name: 'Máximo' },
        { id: TaskFunctionType.MIN, name: 'Mínimo' },
        { id: TaskFunctionType.TOTALIZED, name: 'Totalizado' },
        { id: TaskFunctionType.CUMULATIVE, name: 'Acumulado' },
      ]}
      size='small'
      resettable
    />
    <TextInput
      label='Expresión Instantánea'
      source='InstantExpression'
      size='small'
      resettable
    />
  </Filter>
);

const DataSourceTypeField = ({ record }: any) => {
  // Intentar diferentes formas de acceder al tipo de fuente de datos
  const dataSourceType =
    record?.dataSource?.dataSourceType ||
    record?.dataSourceType ||
    record?.dataSourceDataSourceType ||
    record?.['dataSource.dataSourceType'];

  if (!dataSourceType) {
    return <span>-</span>;
  }

  const typeLabels: Record<string, { label: string; color: string }> = {
    VARIABLE: { label: 'Variable', color: '#1976d2' },
    CSV_COLUMN: { label: 'Columna CSV', color: '#2e7d32' },
  };

  const type = typeLabels[dataSourceType] || {
    label: dataSourceType,
    color: '#757575',
  };

  return (
    <Chip
      label={type.label}
      size='small'
      sx={{
        backgroundColor: type.color,
        color: 'white',
        fontWeight: 500,
      }}
    />
  );
};

const CalculationTypeField = ({ record }: any) => {
  if (!record?.instantCalculationType) return <span>-</span>;

  const calcLabels: Record<string, string> = {
    AVERAGE: 'Promedio',
    SUM: 'Suma',
    MAX: 'Máximo',
    MIN: 'Mínimo',
    TOTALIZED: 'Totalizado',
    ADVANCED_CALCULATION: 'Cálculo Avanzado',
    ADVANCED_CALCULATION_FILTER: 'Cálculo Avanzado con Filtro',
  };

  return (
    <span>
      {calcLabels[record.instantCalculationType] ||
        record.instantCalculationType}
    </span>
  );
};

// Componente interno para manejar el filtro jerárquico
const HierarchicalFilterWrapper = () => {
  const { setFilters, filterValues } = useListContext();
  const [lastPlantId, setLastPlantId] = useState<number | undefined>(undefined);

  const handleFilterChange = (selections: HierarchicalFilterSelections) => {
    console.log('🔍 HierarchicalFilter onChange:', selections);

    // Solo actualizar si realmente cambió el plantId
    if (selections.plantId !== lastPlantId) {
      setLastPlantId(selections.plantId);
      setFilters(
        {
          ...filterValues,
          PlantId: selections.plantId || undefined,
        },
        filterValues
      );
    }
  };

  return (
    <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
      <HierarchicalFilter
        onChange={handleFilterChange}
        levels={['plant']}
        size='small'
      />
    </Box>
  );
};

const SubscriptionList = () => {
  return (
    <Card elevation={1} sx={{ m: 2, p: 2 }}>
      <Box
        sx={{ p: 3, pt: 2, pb: 2.5, borderBottom: 1, borderColor: 'divider' }}
      >
        <Typography
          variant='h5'
          component='h1'
          sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
        >
          <SubscriptionsIcon color='primary' />
          Gestión de Suscripciones
        </Typography>
      </Box>

      <List
        title={false}
        filters={<SubscriptionFilter />}
        actions={<SubscriptionListActions />}
        perPage={25}
        sort={{ field: 'scadaReferenceName', order: 'ASC' }}
        sx={{ '& .RaList-main': { mt: 0 } }}
      >
        <HierarchicalFilterWrapper />
        <Datagrid
          rowClick='show'
          sx={{
            '& .RaDatagrid-headerCell': { fontWeight: 'bold' },
            '& .MuiTableCell-body': {
              minHeight: 65,
              height: '100%',
              verticalAlign: 'middle',
            },
          }}
        >
          <TextField source='id' label='ID' />
          <TextField
            source='scadaReferenceName'
            label='Referencia SCADA'
            sx={{ fontWeight: 500 }}
          />
          <FunctionField
            label='Tipo de Fuente'
            render={(record: any) => <DataSourceTypeField record={record} />}
          />
          <ReferenceField source='plantId' reference='plants' label='Planta'>
            <TextField source='plantName' />
          </ReferenceField>
          <TextField source='unitChange' label='Unidad' />
          <BooleanField
            source='instantEnabled'
            label='Instantáneo'
            valueLabelTrue='Sí'
            valueLabelFalse='No'
          />
          <FunctionField
            label='Tipo de Cálculo'
            render={(record: any) => <CalculationTypeField record={record} />}
          />
          <FunctionField
            source='instantExpression'
            label='Expresión'
            render={(record: any) =>
              formatTimeSpanExpression(record.instantExpression) || '-'
            }
            sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}
          />
          <ShowButton label={''} />
          <EditButton label={''} />
          <DeleteWithConfirmButton resource='Subscripciones' label='Eliminar' />
        </Datagrid>
      </List>
    </Card>
  );
};

export default SubscriptionList;
